/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

// The sanctioned path for strings in a view — see src/viewModel/locale
// (MVVM: model → viewModel → views, enforced by scripts/lint-mvvm.cjs).
import { Locale, LocaleKeys } from "../../../viewModel/locale";
import type { TileBadgesProps } from "./TileBadgesProps";
import "./TileBadges.scss";

/**
 * TileBadges — the strip of status badges overlaid on a piece of artwork.
 *
 * Renders in a fixed priority order — Recording, Live, Upcoming, New, Saved,
 * Watched — so a row of tiles reads consistently however many badges each item
 * happens to carry. Recording is the red dot; the rest are worded chips.
 *
 * Shared deliberately: the strip belongs to the artwork, not to the component
 * that hosts it, and it is drawn on carousel tiles (`Tile`) and on the
 * landscape cards My Hub's Saved, Deleted and series screens stack
 * (`ContentCard`). One renderer means a badge added here appears on both
 * without a second implementation drifting from this one.
 *
 * Returns `null` when nothing applies, so a host can drop it into its artwork
 * unconditionally and not think about the empty case.
 */
const TileBadges: FunctionComponent<TileBadgesProps> = (
  props: TileBadgesProps
): JSX.Element | null => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "TileBadges";
  const {
    isLive = false,
    isUpcoming = false,
    isNew = false,
    isSaved = false,
    isWatched = false,
    isRecording = false,
    isHidden = false,
    classStyle = ""
  } = props;

  const badges: JSX.Element[] = [];

  // Priority 1 — Recording
  if (isRecording) {
    badges.push(
      <div key="recording" className="tile-badge tile-badge--recording">
        <span className="tile-badge__recording-dot" />
      </div>
    );
  }

  // Priority 2 — Live
  if (isLive) {
    badges.push(
      <div key="live" className="tile-badge tile-badge--live">
        {Locale.getString(LocaleKeys.LIVE_BADGE_LABEL)}
      </div>
    );
  }

  // Priority 3 — Upcoming
  if (isUpcoming) {
    badges.push(
      <div key="upcoming" className="tile-badge tile-badge--upcoming">
        {Locale.getString(LocaleKeys.UPCOMING_BADGE_LABEL)}
      </div>
    );
  }

  // Priority 4 — New
  if (isNew) {
    badges.push(
      <div key="new" className="tile-badge tile-badge--new">
        {Locale.getString(LocaleKeys.NEW_BADGE_LABEL)}
      </div>
    );
  }

  // Priority 5 — Saved
  if (isSaved) {
    badges.push(
      <div key="saved" className="tile-badge tile-badge--saved">
        {Locale.getString(LocaleKeys.SAVED_BADGE_LABEL)}
      </div>
    );
  }

  // Priority 6 — Watched
  if (isWatched) {
    badges.push(
      <div key="watched" className="tile-badge tile-badge--watched">
        {Locale.getString(LocaleKeys.WATCHED_BADGE_LABEL)}
      </div>
    );
  }

  if (badges.length === 0) {
    return null;
  }

  const stripClassName: string =
    "tile-badges" +
    (isHidden ? " tile-badges--hidden" : "") +
    (classStyle === "" ? "" : " " + classStyle);

  return <div className={stripClassName}>{badges}</div>;
};

export default TileBadges;
